import { useRouter } from 'next/router';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { UseSearchFiltersFragmentFragmentDoc } from '@packages/gql/generated/shopping/UseSearchFiltersFragmentFragmentDoc';

import { useTracking } from '@packages/tracking';
import type { GTMEventGlycerinApplyFilter } from '@packages/tracking';
import type { SelectedFilterValue } from '../types';
import type { CompressedSelectedFilterValues } from '../queryEncoding';
import {
  decodeSelectedFilterValues,
  compressSelectedFilterValues,
  encodeSelectedFilterValues,
  pruneQuery,
  encodePage,
  decodePage,
  encodeCategoryFilter,
} from '../queryEncoding';
import { decompressSelectedFilterValues } from '../utils/decompressSelectedFilterValues';
import { SEO_FILTER_SEPARATOR } from '../utils/categoryPaths';
import { isFullyContained, mergeSelectedFilterValues } from '../utils/selectedFilterValues';
import { buildGlycerinFilters } from '../utils/tracking/buildGlycerinFilters';

import {
  useSpecialCategoryFilterValue,
  SPECIAL_CATEGORY_FILTER_ID,
} from './useSpecialCategoryFilterValue';

/* GraphQL */ `
  fragment UseSearchFiltersFragment on SearchProductResult {
    ...DecompressSelectedFilterValuesFragment
    ...UseSpecialCategoryFilterValueFragment
    ... on CategoryResult {
      seoUrlFilterPreset {
        filterId
        selectedValueIds
      }
    }
  }
`;

/**
 * Use selected filters persisted in the url query
 *
 * Changing filters results in a router.push, which will trigger a data reload
 */
export const useSearchFilters = (
  maskedData: FragmentType<typeof UseSearchFiltersFragmentFragmentDoc>,
) => {
  const {
    push,
    pathname,
    query: { categories: currentCategories, ...restOfQuery },
  } = useRouter();

  const dispatchGtmEvent = useTracking();

  const data = getFragmentData(UseSearchFiltersFragmentFragmentDoc, maskedData);

  const seoUrlFilterPreset = ('seoUrlFilterPreset' in data && data.seoUrlFilterPreset) || [];

  const selectedSeoFilterValues = decompressSelectedFilterValues(
    seoUrlFilterPreset.reduce(
      (lookup, { filterId, selectedValueIds }) => ({ ...lookup, [filterId]: selectedValueIds }),
      {} as CompressedSelectedFilterValues,
    ),
    data,
  );

  const { selectedCategoryFilterValues } = useSpecialCategoryFilterValue(data);

  const selectedFilterValuesFromQuery = decompressSelectedFilterValues(
    decodeSelectedFilterValues(restOfQuery),
    data,
  );

  const selectedFilterValues = mergeSelectedFilterValues(
    selectedFilterValuesFromQuery,
    selectedSeoFilterValues.concat(selectedCategoryFilterValues),
  );

  const pushFilterChange = (values: SelectedFilterValue[]) => {
    const seoFilterIsStillActive = selectedSeoFilterValues.every((value) =>
      values.some((newValue) => isFullyContained(value, newValue)),
    );

    const isSeoFilterRoute = currentCategories?.includes(SEO_FILTER_SEPARATOR);

    const serpCategoryFilterValue = values.find(
      (value) => value.filterId === SPECIAL_CATEGORY_FILTER_ID,
    );

    const otherValues = values.filter((value) => value !== serpCategoryFilterValue);

    // remove SEO filter from route unless the SEO filter is still fully selected
    const categories =
      isSeoFilterRoute && !seoFilterIsStillActive
        ? currentCategories?.slice(0, currentCategories?.indexOf(SEO_FILTER_SEPARATOR))
        : currentCategories;

    const { pagingMode } = decodePage(restOfQuery);

    const query = pruneQuery({
      ...restOfQuery,
      ...encodeSelectedFilterValues(compressSelectedFilterValues(otherValues)),
      ...(pagingMode !== 'infinite' && encodePage()), // every filter change resets page counter unless infinite scrolling is active
      categories,
      // always overwrite any existing category filter, to make sure it can be properly removed
      ...encodeCategoryFilter(serpCategoryFilterValue?.selectedValues?.[0]),
    });

    const filtersPayload = buildGlycerinFilters(
      values.map((selectedValues) => ({
        id: selectedValues.filterId,
        valueIds: selectedValues.selectedValues,
        range: selectedValues.selectedRange?.range,
      })),
    );

    if (filtersPayload.length > 0) {
      dispatchGtmEvent<GTMEventGlycerinApplyFilter>({
        event: 'ApplyFilter',
        _clear: true,
        ApplyFilterData: {
          filters: filtersPayload,
        },
      });
    }

    push(
      {
        // currently the /c/ prefix is internal-only, and rewritten in the middleware
        pathname: pathname.replace(/^\/c/, '').concat('/'),
        query,
      },
      undefined,
      { scroll: false },
    );
  };

  return {
    selectedFilterValues,
    pushFilterChange,
  };
};
